import { MEDAL_EVENT_STATES, type MedalEventState, type MedalProgressCounts, type MedalProgressSport } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "../../lib/api/messages";
import { progressQuery } from "../../lib/desk/queries";
import { formatInteger } from "../../lib/i18n/format";
import { ChartFrame, ChartTable } from "../charts/chart-frame";
import { Legend } from "../charts/legend";
import { StackedBars } from "../charts/stacked-bar";
import { Card } from "../page/card";
import { Spinner } from "../ui/spinner";
import { STATE_COLOR } from "./state-chip";

/**
 * Progress by sport — `GET /medals/progress` (ONE statement, the shared state
 * builder): one stacked bar per sport, its medal events by state in pipeline
 * order on one scale, so a big sport reads big and a finished one reads
 * green-and-violet. Replaces the nine-column table; the table view keeps every
 * number (and the medal counts). One sport only when `sportId` is given.
 */

const KEY: Record<MedalEventState, keyof MedalProgressCounts> = {
	NOT_SCHEDULED: "not_scheduled",
	COMPETING: "competing",
	WAITING: "waiting",
	AWARDED: "awarded",
	NO_MEDALS: "no_medals",
};

export function ProgressBySport({
	gamesId,
	sportId,
	label,
}: {
	gamesId: string;
	sportId?: string | undefined;
	/** A sport's row label (a link to its filtered view); its name by default. */
	label?: (sport: MedalProgressSport) => ReactNode;
}) {
	const { t } = useTranslation("medals");
	const progress = useQuery(progressQuery(gamesId));
	const sports = progress.data ? progress.data.sports.filter((s) => (sportId ? s.sport_id === sportId : s.events > 0)) : [];

	return (
		<Card className="mt-[1rem] px-[1rem] py-[0.875rem]">
			<ChartFrame
				interactive={label !== undefined}
				title={t("progress.bySport")}
				description={t("progress.bySportSubtitle")}
				legend={<Legend items={MEDAL_EVENT_STATES.map((s) => ({ key: s, label: t(`state.${s}`), color: STATE_COLOR[s], value: progress.data ? formatInteger(progress.data.totals[KEY[s]]) : undefined }))} />}
				table={
					progress.data && (
						<ChartTable
							head={[
								t("progress.col.sport"),
								t("progress.col.events"),
								...MEDAL_EVENT_STATES.map((s) => t(`state.${s}`)),
								t("progress.col.medalsAwarded"),
								t("progress.col.medalsToAward"),
							]}
							rows={[...sports, ...(sportId ? [] : [{ ...progress.data.totals, name: t("progress.allSports"), sport_id: "all", code: "" }])].map((s) => [
								s.name,
								formatInteger(s.events),
								...MEDAL_EVENT_STATES.map((st) => formatInteger(s[KEY[st]])),
								formatInteger(s.medals_awarded),
								formatInteger(s.medals_to_award),
							])}
						/>
					)
				}
			>
				{progress.isPending ? (
					<Spinner className="size-[1.25rem] text-board" />
				) : progress.isError ? (
					<p className="text-conflict">{errorMessage(progress.error)}</p>
				) : sports.length === 0 ? (
					<p className="text-[0.875rem] text-muted">{t("progress.empty")}</p>
				) : (
					<StackedBars
						labelWidth="12rem"
						rows={sports.map((s) => ({
							key: s.sport_id,
							title: s.name,
							label: label ? label(s) : s.name,
							note: t("progress.decidedOf", { n: formatInteger(s.awarded + s.no_medals), total: formatInteger(s.events) }),
							segments: MEDAL_EVENT_STATES.map((st) => ({ key: st, label: t(`state.${st}`), color: STATE_COLOR[st], value: s[KEY[st]] })),
						}))}
					/>
				)}
			</ChartFrame>
		</Card>
	);
}
